import React from "react";
import { GetAssetService } from "../services/AssetService";
import { PiAsset } from "../Types/PiAsset";

export type AssetCapability = PiAsset["Capabilities"][number];

export type AssetTreeState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "ready"; asset: PiAsset };

/** "Name - Alias", else "Name - Description", else "Name". */
export const formatAssetLabel = (asset: PiAsset) =>
    asset.Alias ? `${asset.Name} - ${asset.Alias}` : asset.Description ? `${asset.Name} - ${asset.Description}` : asset.Name;

/** Children of an asset, tolerating a missing list or empty entries. */
export const getAssetChildren = (asset: PiAsset): PiAsset[] =>
    Array.isArray(asset.Children) ? asset.Children.filter(Boolean) : [];

/** Capabilities of an asset, tolerating a missing list or empty entries. */
export const getAssetCapabilities = (asset: PiAsset): AssetCapability[] =>
    Array.isArray(asset.Capabilities) ? asset.Capabilities.filter((capability) => capability && capability.Name) : [];

/** Rows below the root without any capability are shown muted, as they offer nothing to do. */
export const isAssetMuted = (asset: PiAsset, _parent: PiAsset | undefined, depth: number) =>
    depth > 0 && getAssetCapabilities(asset).length === 0;

/**
 * The asset tree for the drawers, fetched once. A failed request (GetAssetService then returns a placeholder without a
 * name) or an unexpected response is reported as an error rather than rendered as a tree.
 */
export function useAssetTree(): AssetTreeState {
    const [state, setState] = React.useState<AssetTreeState>({ status: "loading" });

    React.useEffect(() => {
        let active = true;
        GetAssetService().then((asset) => {
            if (active) {
                setState(asset && asset.Name ? { status: "ready", asset } : { status: "error" });
            }
        });
        return () => {
            active = false;
        };
    }, []);

    return state;
}

/** Placeholder row for the drawer while the asset tree loads or after it failed to. */
export function AssetTreeStatus({ status }: { status: "loading" | "error" }) {
    return (
        <p role="status" className="px-2 py-1.5 text-sm text-[color:var(--colorNeutralForeground3)]">
            {status === "loading" ? "Loading assets…" : "Assets could not be loaded."}
        </p>
    );
}
